import { apiClient } from './client'
import type { PageResponse } from '@/types/common'
import type { ArticleSummary, ArticleDetail, WordPopup } from '@/types/content'
import type { PracticeUnitRef } from '@/types/practiceUnit'

export type ArticleReadStatus = 'ALL' | 'READ' | 'UNREAD'

export interface ArticleFilters {
  cefrLevel?: string
  search?: string
  tag?: string
  readStatus?: ArticleReadStatus
  page?: number
  size?: number
}

export interface CreateArticlePayload {
  title: string
  content: string
  cefrLevel: string
  difficulty?: number
  estimatedTime?: number
  imageUrl?: string
  author?: string
  sourceUrl?: string
  published: boolean
  tags?: string[]
}

export interface GenerateArticlePayload {
  cefrLevel: string
  difficulty?: number
  estimatedTime?: number
  author?: string
  topic?: string
  tags?: string[]
  avoidThemes?: string[]
}

export interface GeneratedArticleDraft {
  title: string
  content: string
}

export interface ArticleInventorySuggestion {
  suggestion: {
    cefrLevel: string
    estimatedTime: number
    difficulty: number
    tags: string[]
    avoidThemes: string[]
  }
  scarcity: Array<{ cefrLevel: string; estimatedTime: number; count: number }>
  tagCoverage: Array<{ tag: string; count: number }>
  reason: string
}

export interface GeneratePersonalizedArticlePayload {
  units?: PracticeUnitRef[]
  /** @deprecated Prefer units */
  wordIds?: string[]
  cefrLevel?: string
  difficulty?: number
  estimatedTime?: number
  tags?: string[]
}

export const articlesApi = {
  getArticles: (filters: ArticleFilters = {}) => {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, v]) => v !== undefined && v !== '')
    )
    return apiClient.get<PageResponse<ArticleSummary>>('/articles', { params })
  },

  getArticle: (id: string) =>
    apiClient.get<ArticleDetail>(`/articles/${id}`),

  markAsRead: (id: string) =>
    apiClient.post<{ completedAt: string }>(`/articles/${id}/read`),

  getWordPopup: (articleId: string, word: string, context?: string) =>
    apiClient.get<WordPopup>(`/articles/${articleId}/word-popup`, {
      params: { word, ...(context ? { context } : {}) },
    }),

  getTags: () =>
    apiClient.get<string[]>('/articles/tags'),

  /** AIPRAC-04 — Generate comprehension questions for an article (async). */
  generateComprehension: (articleId: string) =>
    apiClient.post<{ jobId: string }>(`/articles/${articleId}/comprehension`),

  checkComprehension: (
    articleId: string,
    answers: Array<{ question: string; type?: string; answer: string; confidence?: string }>
  ) =>
    apiClient.post<{ jobId: string }>(`/articles/${articleId}/comprehension/check`, { answers }),

  generatePersonalized: (data: GeneratePersonalizedArticlePayload) =>
    apiClient.post<{ jobId: string }>('/articles/generate', data),

  // Admin
  getInventorySuggestions: () =>
    apiClient.get<ArticleInventorySuggestion>('/admin/articles/inventory-suggestions').then((r) => r.data),

  getRecentTitles: (cefrLevel: string) =>
    apiClient
      .get<string[]>('/admin/articles/recent-titles', { params: { cefrLevel } })
      .then((r) => r.data),

  generateArticleDraft: (data: GenerateArticlePayload) =>
    apiClient.post<GeneratedArticleDraft>('/admin/articles/generate', data).then((r) => r.data),

  createArticle: (data: CreateArticlePayload) =>
    apiClient.post<ArticleSummary>('/admin/articles', data),
}

